import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DollarSign, Loader2 } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString()}`;

export default function Earnings() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/earnings").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);

  if (!rows) return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  const total = rows.reduce((a, b) => a + b.amount, 0);
  const personal = rows.filter((r) => (r.level ?? 0) === 0).reduce((a, b) => a + b.amount, 0);
  const overrides = total - personal;
  const paidOut = rows.filter((r) => r.paid).reduce((a, b) => a + b.amount, 0);
  const pending = total - paidOut;

  return (
    <div data-testid="earnings-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-patriot font-bold">Income</p>
        <h1 className="font-display text-3xl text-navy mt-1">Earnings</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <div className="bg-navy-deep text-white rounded-xl p-6 afha-grain">
          <p className="text-white/60 text-sm">Total earnings</p>
          <p className="font-display text-4xl font-black mt-1" data-testid="earnings-total">{fmt(total)}</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm">Personal sales income</p>
          <p className="font-display text-4xl font-bold text-navy mt-1" data-testid="earnings-personal">{fmt(personal)}</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-6">
          <p className="text-muted-foreground text-sm">Override income</p>
          <p className="font-display text-4xl font-bold text-navy mt-1" data-testid="earnings-overrides">{fmt(overrides)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
        <div className="bg-green-50 border border-green-200 rounded-xl p-5">
          <p className="text-green-700 text-sm font-medium">Paid out to you</p>
          <p className="font-display text-3xl font-bold text-green-700 mt-1" data-testid="earnings-paid">{fmt(paidOut)}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-amber-700 text-sm font-medium">Pending payout</p>
          <p className="font-display text-3xl font-bold text-amber-700 mt-1" data-testid="earnings-pending">{fmt(pending)}</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-dashed border-border rounded-xl p-16 text-center" data-testid="earnings-empty">
          <DollarSign className="h-12 w-12 text-navy/20 mx-auto mb-4" />
          <h3 className="font-display text-xl text-navy">No earnings yet</h3>
          <p className="text-muted-foreground mt-2">Earnings appear here when you or your team onboard physicians.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="earnings-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-navy font-semibold">Physician</TableHead>
                <TableHead className="text-navy font-semibold">Onboarded by</TableHead>
                <TableHead className="text-navy font-semibold">Source</TableHead>
                <TableHead className="text-navy font-semibold">Status</TableHead>
                <TableHead className="text-navy font-semibold text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const lvl = r.level ?? 0;
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium text-navy">{r.physician_name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.source_rep_name}</TableCell>
                    <TableCell>
                      {lvl === 0 ? (
                        <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-patriot/10 text-patriot text-xs font-semibold">Personal</span>
                      ) : (
                        <span className="inline-flex items-center justify-center h-6 min-w-6 px-2 rounded-full bg-navy/5 text-navy text-xs font-mono font-bold">L{lvl}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.paid ? (
                        <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold" data-testid="earning-paid-badge">Paid</span>
                      ) : (
                        <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold" data-testid="earning-pending-badge">Pending</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-navy">{fmt(r.amount)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
